import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconButton, Typography, Box, Grid, SwipeableDrawer, List, ListItem, ListItemText, InputBase, useTheme, Skeleton, CircularProgress } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import LineWeightOutlinedIcon from '@mui/icons-material/LineWeightOutlined';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import defaultBook from "../../Images/defaultBook.png";
import BooksService from '../../Services/Books';
import KalyansService from '../../Services/Kalyan';
import KalpataruServices from '../../Services/Kalpataru';
import VivekVaniService from '../../Services/Vivekvani';
import GeetGovindServices from '../../Services/GeetGovind'; // NEW: Import GeetGovindServices
import ArticlesService from '../../Services/Articles';
import ArticleDummyImg from "../../Images/Image/article.png"; // Dummy image for articles
import { Product } from './MobileMagazines';
// --- Interfaces for Styled Component Props ---
interface ViewProps {
    isGridView: boolean;
}

// --- Styled Components ---
export const HeaderBox = styled(Box)<{ isSearchActive?: boolean }>(({ theme, isSearchActive }) => ({
    width: "100%",
    backgroundColor: "#f97316",
    height: "90px",
    flexShrink: 0,
    position: "relative",
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...(isSearchActive && {
        justifyContent: 'center',
        alignItems: 'center',
    }),
}));

const FilterBarBox = styled(Box)(({ theme }) => ({
    backgroundColor: "white",
    height: '35px',
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    borderBottom: '1px solid #eee',
    paddingX: theme.spacing(1),
}));

const FilterOptionItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
}));

export const MainContentBox = styled(Box)(({ theme }) => ({
    flexGrow: 1,
    overflowY: 'auto',
    paddingBottom: '60px',
    '&::-webkit-scrollbar': { display: 'none' },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
}));

export const BookGridContainer = styled(Grid, {
    shouldForwardProp: (prop) => prop !== 'isGridView'
})<ViewProps>(({ theme, isGridView }) => ({
    padding: theme.spacing(1),
    justifyContent: isGridView ? 'flex-start' : 'flex-start',
    flexDirection: isGridView ? 'row' : 'column',
}));

export const BookItemBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isGridView'
})<ViewProps>(({ theme, isGridView }) => ({
    backgroundColor: "white",
    padding: theme.spacing(1),
    textAlign: isGridView ? "center" : "left",
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: isGridView ? 'column' : 'row',
    height: isGridView ? '100%' : 'auto',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    alignItems: isGridView ? 'stretch' : 'center',
    gap: isGridView ? 0 : theme.spacing(1.5),
    marginBottom: isGridView ? 0 : theme.spacing(1),
    cursor: 'pointer',
}));

export const BookImage = styled('img', {
    shouldForwardProp: (prop) => prop !== 'isGridView'
})<ViewProps>(({ isGridView }) => ({
    width: isGridView ? "100%" : "60px",
    height: isGridView ? "120px" : "80px",
    objectFit: "contain",
    marginBottom: isGridView ? '8px' : '0',
    flexShrink: 0,
}));

export const BookTitleTypography = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'isGridView'
})<ViewProps>(({ isGridView }) => ({
    fontWeight: "bold",
    color: "#4b2e2e",
    fontSize: isGridView ? "12px" : "14px",
    display: "-webkit-box",
    WebkitLineClamp: isGridView ? 2 : 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-word",
    flexGrow: 1,
}));

export const FixedBottomBar = styled(Box)(({ theme }) => ({
    width: "100%",
    height: "40px",
    backgroundColor: "#f97316",
    position: "fixed",
    bottom: 0,
    left: 0,
    padding: theme.spacing(1),
}));

const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    width: '90%',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.up('sm')]: {
        width: '70%',
    },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 1),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    width: '100%',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, `calc(1em + ${theme.spacing(3)})`),
        transition: theme.transitions.create('width'),
        color: 'white',
        width: '100%',
    },
}));

export const SkeletonBookGridContainer = styled(Grid, {
    shouldForwardProp: (prop) => prop !== 'isGridView'
})<ViewProps>(({ theme, isGridView }) => ({
    padding: theme.spacing(1),
    justifyContent: isGridView ? 'center' : 'flex-start',
    flexDirection: isGridView ? 'row' : 'column',
}));

export const SkeletonBookItemBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isGridView'
})<ViewProps>(({ theme, isGridView }) => ({
    backgroundColor: "white",
    padding: theme.spacing(1),
    textAlign: isGridView ? "center" : "left",
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: isGridView ? 'column' : 'row',
    height: isGridView ? '100%' : 'auto',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    alignItems: isGridView ? 'stretch' : 'center',
    gap: isGridView ? 0 : theme.spacing(1.5),
    marginBottom: isGridView ? 0 : theme.spacing(1),
}));

const PAGE_SIZE = 20;

const MobileBookPage = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const location: any = useLocation();
    const [items, setItems] = useState<any[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [activeSortOption, setActiveSortOption] = useState(location?.state?.sortOption || '3');
    const [isSortDrawerOpen, setIsSortDrawerOpen] = useState(false);
    const [isGridView, setIsGridView] = useState(true);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const observerRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const isShowFilter = false; // set true to restore filter/sort bar

    // Check if it's a Kalyan/Kalpataru/Vivek Vani/Geet Govind page
    const isVivekVaniListingPath = location.pathname.startsWith('/magazines/vivekvani');
    const isKalyansPage = !!location?.state?.kalyansId || !!location?.state?.allKalyans;
    const isKalpataruBook = !!location?.state?.isKalpataruBook;
    const isVivekVaniPage = isVivekVaniListingPath || !!location?.state?.vivekVaniId;
    const resolvedVivekVaniId = location?.state?.vivekVaniId || (isVivekVaniListingPath ? Product.VivekVani : "");
    const isKalpataruId = !!location?.state?.kalpataruId || !!location?.state?.allKalpataru;
    const kalpataruId = location?.state?.kalpataruId || "";
    const isArticeleCategoryId = !!location?.state?.articleCategoryId;
    // const isSelectedAuthorId = !!location?.state?.selectedAuthorsId;
    const { selectedAuthorId, selectedLanguageId, selectedCategoryId } = location.state || {};
    const isArticleProductType = location?.state?.productType === "article";
    // const isAllArticles = !!location?.state?.allArticles;
    
    // const isGeetGovindPage = !!location?.state?.isGeetGovind; // NEW: Flag for Geet Govind
    const [searchParams] = useSearchParams()
    const isAllArticles = location?.state?.allArticles || searchParams.get("allArticles") === "true";
    // const magazineType = searchParams.get('type');
    const magazineType = location?.state?.type

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchQuery]);

    const fetchItems = useCallback(async (pageNum: number, append: boolean) => {
        if (append) setIsLoadingMore(true);
        else setIsLoading(true);

        try {
            let res: any;
            const offset = pageNum * PAGE_SIZE;
            const rawLanguageId = location?.state?.selectedLanguageId || location?.state?.kalyansLanguage || location?.state?.magazineLanguage || location?.state?.vivekVaniLanguage || location?.state?.magazineLang || "";
            const resolvedLanguageId = (rawLanguageId === "hi" || rawLanguageId === "en") ? "" : rawLanguageId;
            const commonParams = {
                offset,
                limit: PAGE_SIZE,
                isFeatured: false,
                searchQuery: debouncedSearchQuery,
                sortOption: activeSortOption,
                languageId: resolvedLanguageId,
                isSpecial: window.location.pathname === "/books/special" || window.location.pathname === "/magazines/vivekvani/special",
                categoryId: location?.state?.selectedCategoryId || location?.state?.categoryId || location?.state?.articleCategoryId || "",
                authorId: location?.state?.selectedAuthorId || location?.state?.selectedAuthorsId || "",
                subjectId: location?.state?.selectedSubjectId || "",
                userId: ""
            };

            if (isKalyansPage) {
                const kalyansId = location?.state?.kalyansId || "";
                if (isKalpataruBook) {
                    res = await KalpataruServices.getKalyansKalpataru(
                        commonParams.offset, commonParams.limit, false, "", "", commonParams.searchQuery, commonParams.sortOption, commonParams.languageId, false
                    );
                } else {
                    res = await KalyansService.getKalyans(
                        commonParams.offset, commonParams.limit, commonParams.isFeatured,
                        kalyansId, "", commonParams.searchQuery, commonParams.sortOption,
                        commonParams.languageId, commonParams.isSpecial
                    );
                }
            } else if (isVivekVaniPage) {
                res = await VivekVaniService?.getVanis(
                    commonParams.offset, commonParams.limit, resolvedVivekVaniId || "",
                    commonParams.isFeatured, commonParams.sortOption, "",
                    commonParams.searchQuery, commonParams.languageId,
                    commonParams.isSpecial
                );
            } else if (magazineType === 'geetGovind') {
                res = await GeetGovindServices.getMonthlyMagazine(
                    commonParams.searchQuery, commonParams.offset, commonParams.limit,
                    commonParams.categoryId, false, commonParams.sortOption,
                    commonParams.authorId, commonParams.languageId,
                    commonParams.isSpecial, commonParams.subjectId, commonParams.userId
                );
            } else if (isKalpataruId) {
                res = await KalpataruServices.getKalyansKalpataru(
                    commonParams.offset, commonParams.limit, false, kalpataruId, "", commonParams.searchQuery, commonParams.sortOption, commonParams.languageId, commonParams.isSpecial
                );
            } else if (isArticeleCategoryId || isArticleProductType || isAllArticles) {
                const articleCategoryId = location?.state?.articleCategoryId;
                res = await ArticlesService.getArticles(
                    commonParams.offset,
                    commonParams.limit,
                    false,
                    articleCategoryId || "",
                    selectedAuthorId || "",
                    "",
                    commonParams.sortOption,
                    commonParams.searchQuery,
                    false
                );
            } else {
                const selectedCategoryId = location?.state?.selectedCategoryId || location?.state?.categoryId || "";
                const selectedAuthorId = location?.state?.selectedAuthorId || "";
                res = await BooksService.getBooks(
                    commonParams.offset, commonParams.limit, commonParams.isFeatured,
                    selectedCategoryId, selectedAuthorId, commonParams.searchQuery,
                    commonParams.sortOption, commonParams.languageId, commonParams.isSpecial
                );
            }

            if (res?.status && res.result) {
                const newItems = (res.result.items || []).map((item: any) => ({
                    title: item.name,
                    description: (isArticeleCategoryId || isArticleProductType) && item.articleContent,
                    img: item.bookThumbPath || item.kalyanThumbPath || item.kalyanKalpataruThumbPath || item.vivekVaniThumbPath || item.monthlyMagazineThumbPath || ArticleDummyImg || defaultBook,
                    author: item.author,
                    id: item.id,
                    slug: item.slug,
                }));
                const total = res.result.totalRecords || 0;

                if (append) {
                    setItems(prev => [...prev, ...newItems]);
                } else {
                    setItems(newItems);
                }
                setTotalItems(total);
                setHasMore((pageNum * PAGE_SIZE + newItems.length) < total);
            } else if (!append) {
                setItems([]);
                setTotalItems(0);
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error fetching items:", error);
            if (!append) {
                setItems([]);
                setTotalItems(0);
                setHasMore(false);
            }
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [
        debouncedSearchQuery,
        activeSortOption,
        location.state,
        location.pathname,
        i18n.language,
        isKalyansPage,
        isKalpataruBook,
        isVivekVaniPage,
        resolvedVivekVaniId,
        magazineType,
        isKalpataruId,
        isArticeleCategoryId,
        isArticleProductType,
        isAllArticles,
        selectedAuthorId,
        kalpataruId,
    ]);

    useEffect(() => {
        setPage(0);
        setHasMore(true);
        fetchItems(0, false);
    }, [fetchItems]);

    const loadMore = useCallback(async () => {
        if (!hasMore || isLoadingMore || isLoading) return;
        const nextPage = page + 1;
        setPage(nextPage);
        await fetchItems(nextPage, true);
    }, [page, hasMore, isLoadingMore, isLoading, fetchItems]);

    useEffect(() => {
        const node = observerRef.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isLoading) {
                    loadMore();
                }
            },
            { threshold: 0.1 }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [loadMore, hasMore, isLoadingMore, isLoading]);
    const handleSortOptionSelect = (optionValue: string) => {
        setActiveSortOption(optionValue);
        setIsSortDrawerOpen(false);
    };

    const handlePopularLatestToggle = () => {
        setActiveSortOption((prevOption: string) => {
            if (prevOption === '3') {
                return '1';
            } else if (prevOption === '1') {
                return '3';
            }
            return '3';
        });
    };

    const toggleSortDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (
            event &&
            event.type === 'keydown' &&
            ((event as React.KeyboardEvent).key === 'Tab' ||
                (event as React.KeyboardEvent).key === 'Shift')
        ) {
            return;
        }
        setIsSortDrawerOpen(open);
    };

    const handleSearchToggle = () => {
        setIsSearchActive(prev => !prev);
        if (!isSearchActive) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 0);
        } else {
            setSearchQuery("");
            setDebouncedSearchQuery("");
        }
    };

    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };

    const handleItemClick = (itemId: string, itemSlug?: string) => {
        if (isArticeleCategoryId || isArticleProductType || isAllArticles) {
            navigate('/readArticles', { state: { articleId: itemId } });
        } else {
            navigate(`/books/${itemSlug}`);
        }
    };

    const sortOptions = [
        { text: "Default", value: "0" },
        { text: "Latest", value: "1" },
        { text: "Alphabetical", value: "2" },
        { text: "Popular", value: "3" },
    ];

    const filterOptionsBar = [
        { text: "Sort By", icon: <SwapVertIcon />, type: "action", onClick: toggleSortDrawer(true) },
        { text: "Filter", icon: <FilterAltOutlinedIcon />, type: "static", onClick: () => navigate(isAllArticles !==true? "/filter" : "/filter", { state: { allArticles: isAllArticles } }) },
        {
            text: activeSortOption === '1' ? "Popular" : "Latest",
            icon: <LineWeightOutlinedIcon />,
            type: "sort-toggle",
            onClick: handlePopularLatestToggle
        }
    ];

    const numberOfSkeletons = isGridView ? 9 : 5;
    function stripHtmlTags(htmlString: any) {
        const doc = new DOMParser().parseFromString(htmlString, 'text/html');
        return doc.body.textContent || "";
    }
    return (
        <Box sx={{
            backgroundColor: "#F2F0EF",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }}>
            {/* --- Header Section --- */}
            <HeaderBox isSearchActive={false}>
                <>
                    <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', left: 16, top: 35, p: 0 }}>
                        <ArrowBackIosIcon sx={{ color: "white", fontSize: '18px' }} />
                    </IconButton>
                    <IconButton onClick={() => navigate("/")} sx={{ position: 'absolute', left: 48, top: 35, p: 0 }}>
                        <HomeOutlinedIcon sx={{ color: "white", fontSize: '24px' }} />
                    </IconButton>

                    <IconButton
                        onClick={() => setIsGridView(prev => !prev)}
                        sx={{ position: 'absolute', right: 16, top: 35, p: 0 }}
                    >
                        {isGridView ? (
                            <ViewListIcon sx={{ color: "white", fontSize: '20px' }} />
                        ) : (
                            <ViewModuleIcon sx={{ color: "white", fontSize: '20px' }} />
                        )}
                    </IconButton>
                    <Typography variant="caption" sx={{ color: "#ffffffdd", opacity: 0.9, mb: 0.5 }}>
                        {t("shrihari_tr")}
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#ffffffdd", opacity: 0.9, fontSize: 15 }}>
                        {(() => {
                            let baseTitle = "All_Books";
                            if (isKalyansPage) {
                                baseTitle = isKalpataruBook ? "Kalpataru_lang_tr" : "All_Kalyans";
                            } else if (isKalpataruId) {
                                baseTitle = "Kalpataru_lang_tr";
                            } else if (isVivekVaniPage) {
                                baseTitle = "All_Vivek_Vani";
                            } else if (magazineType) {
                                baseTitle = location?.state?.geetGovindLang === 'english'
                                    ? "itemtype_geetgovind_english_tr"
                                    : "itemtype_geetgovind_hindi_tr";
                            } else if (isArticeleCategoryId || isAllArticles || isArticleProductType) {
                                baseTitle = "All_Article_tr";
                            }
                            const selectedName = location?.state?.categoryName
                                || location?.state?.kalyanName
                                || location?.state?.kalpataruName
                                || location?.state?.vivekVaniName
                                || location?.state?.articleCategoryName
                                || location?.state?.authorName
                                || location?.state?.languageName
                                || null;
                            const titleText = t(baseTitle);
                            return selectedName ? `${titleText} (${selectedName})` : titleText;
                        })()}
                    </Typography>
                </>
            </HeaderBox>

            {/* --- Filter/Sort Bar Section --- */}
            {isShowFilter && (
                <FilterBarBox>
                    {filterOptionsBar.map((option, index) => (
                        <React.Fragment key={option.text}>
                            <FilterOptionItem
                                onClick={option.onClick}
                                sx={{
                                    cursor: option.type === 'action' || option.type === 'sort-toggle' ? 'pointer' : 'default'
                                }}
                            >
                                {React.cloneElement(option.icon, { sx: { color: "lightslategray", fontSize: '20px' } })}
                                <Typography variant="body2" sx={{ color: "lightslategray", fontSize: '13px' }}>
                                    {t(option.text)}
                                </Typography>
                            </FilterOptionItem>
                            {index < filterOptionsBar.length - 1 && <Box sx={{ width: '1px', height: '60%', backgroundColor: '#e0e0e0' }} />}
                        </React.Fragment>
                    ))}
                </FilterBarBox>
            )}

            {/* --- Main Content Area: Displays Items or Skeleton --- */}
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 0.5, fontWeight: 500, fontFamily: "chanakyaUni", fontSize: 16, height: 15, padding: -3 }}>
                Total Records: {totalItems}
            </Typography>

            <MainContentBox>

                {isLoading ? (
                    <SkeletonBookGridContainer container spacing={1} isGridView={isGridView}>
                        {Array.from(new Array(numberOfSkeletons)).map((_, index) => (
                            <Grid
                                item
                                xs={isGridView ? 4 : 12}
                                sm={isGridView ? 3 : 12}
                                md={isGridView ? 2 : 12}
                                key={index}
                            >
                                <SkeletonBookItemBox isGridView={isGridView}>
                                    <Skeleton
                                        variant="rectangular"
                                        width={isGridView ? "100%" : "60px"}
                                        height={isGridView ? "120px" : "80px"}
                                        sx={{
                                            marginBottom: isGridView ? '8px' : '0',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Skeleton variant="text" sx={{ fontSize: isGridView ? '12px' : '14px', width: '80%', mb: 0.5 }} />
                                        {!isGridView && <Skeleton variant="text" sx={{ fontSize: '12px', width: '60%' }} />}
                                    </Box>
                                </SkeletonBookItemBox>
                            </Grid>
                        ))}
                    </SkeletonBookGridContainer>
                ) : items.length > 0 ? (
                    <BookGridContainer container spacing={isGridView ? 1 : 0} isGridView={isGridView}>
                        {items.map((item: any) => (
                            <Grid
                                item
                                xs={isGridView ? 4 : 12}
                                sm={isGridView ? 3 : 12}
                                md={isGridView ? 2 : 12}
                                key={item.id}
                            >
                                <BookItemBox isGridView={isGridView} onClick={() => handleItemClick(item.id, item.slug)}>
                                    <BookImage
                                        src={item.img || defaultBook}
                                        alt={item.title}
                                        onError={(e: any) => { e.currentTarget.src = defaultBook; }}
                                        isGridView={isGridView}
                                    />

                                    <BookTitleTypography variant="body2" isGridView={isGridView}>
                                        {isGridView ? (
                                            item.title
                                        ) : (
                                            <>
                                                {item.title}
                                                <p style={{ fontWeight: 400, fontSize: isGridView ? '10px' : '12px', color: '#4b2e2e', marginTop: 4 }}>
                                                    {stripHtmlTags(item.description).length > 100
                                                        ? stripHtmlTags(item.description).substring(0, 100) + '...'
                                                        : stripHtmlTags(item.description)
                                                    }
                                                </p>
                                            </>
                                        )}
                                    </BookTitleTypography>




                                </BookItemBox>

                            </Grid>
                        ))}
                    </BookGridContainer>
                ) : (
                    <Typography variant="body1" sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                        {t("result_not_found_tr")}
                    </Typography>
                )}

                {isLoadingMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}
                {hasMore && <Box ref={observerRef} sx={{ height: 1 }} />}
            </MainContentBox>

            {/* --- Fixed Bottom Bar --- */}
            {/* <FixedBottomBar /> */}

            {/* --- Sort Options Drawer (Bottom Sheet) --- */}
            {isShowFilter && (
                <SwipeableDrawer
                    anchor="bottom"
                    open={isSortDrawerOpen}
                    onClose={toggleSortDrawer(false)}
                    onOpen={toggleSortDrawer(true)}
                >
                    <Box
                        sx={{ width: 'auto', pb: 2 }}
                        role="presentation"
                        onClick={toggleSortDrawer(false)}
                        onKeyDown={toggleSortDrawer(false)}
                    >
                        <List>
                            <ListItem>
                                <ListItemText primary={<Typography variant="h6" sx={{ fontWeight: 'bold' }}>{t("Sort By")}</Typography>} />
                            </ListItem>
                            {sortOptions.map((option) => (
                                <ListItem
                                    button
                                    key={option.value}
                                    onClick={() => handleSortOptionSelect(option.value)}
                                    sx={{
                                        backgroundColor: activeSortOption === option.value ? '#f0f0f0' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: '#e0e0e0',
                                        },
                                    }}
                                >
                                    <ListItemText primary={t(option.text)} />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </SwipeableDrawer>
            )}
        </Box>
    );
}

export default MobileBookPage;